import 'bootstrap/dist/css/bootstrap.min.css';
import 'datatables.net';
import 'datatables.net-buttons';
import 'datatables.net-buttons-dt/css/buttons.dataTables.min.css';
import 'datatables.net-buttons/js/buttons.colVis';
import 'datatables.net-buttons/js/buttons.html5';
import 'datatables.net-buttons/js/buttons.print';
import Swal from 'sweetalert2';
import $ from 'jquery';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { baseurl } from '../utility/helper';

const AdminList = () => {
  const roleid = parseInt(sessionStorage.getItem("userroleId"));
  const [admins, setAdmins] = useState([]);
  const tableRef = useRef(null);
  const dtRef = useRef(null);
  const navigate = useNavigate();

  // ── Fetch admins sorted by rank ──────────────────────────────────────────────
  const fetchAdmin = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${baseurl}/admin/listadmin`, {
        method: 'GET',
        headers: { 'Authorization': token, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      // Sort by rank ascending (nulls last)
      const sorted = [...data].sort((a, b) => {
        if (a.rank == null && b.rank == null) return 0;
        if (a.rank == null) return 1;
        if (b.rank == null) return -1;
        return a.rank - b.rank;
      });
      setAdmins(sorted);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  }, []);

  useEffect(() => { fetchAdmin(); }, [fetchAdmin]);

  // ── Toggle show_on_website ───────────────────────────────────────────────────
  const toggleVisibility = useCallback(async (adminId, currentValue) => {
    const newValue = currentValue ? 0 : 1;
    try {
      const token = sessionStorage.getItem("token");
      await fetch(`${baseurl}/admin/toggle-visibility/${adminId}`, {
        method: 'PUT',
        headers: { 'Authorization': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_on_website: newValue }),
      });
      setAdmins(prev =>
        prev.map(a => a.admin_id === adminId ? { ...a, show_on_website: newValue } : a)
      );
    } catch (err) {
      console.error('Error toggling visibility:', err);
      Swal.fire('Error!', 'Could not update visibility.', 'error');
    }
  }, []);

  // ── Update rank on server ────────────────────────────────────────────────────
  const updateRank = useCallback(async (adminId, newRank) => {
    try {
      const token = sessionStorage.getItem("token");
      await fetch(`${baseurl}/admin/update-rank/${adminId}`, {
        method: 'PUT',
        headers: { 'Authorization': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rank: newRank }),
      });
    } catch (err) {
      console.error('Error updating rank:', err);
    }
  }, []);

  // ── Move admin up/down in rank ───────────────────────────────────────────────
  const moveRank = useCallback(async (adminId, direction) => {
    setAdmins(prev => {
      const idx = prev.findIndex(a => a.admin_id === adminId);
      if (idx === -1) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= prev.length) return prev;

      const updated = [...prev];
      // Swap
      [updated[idx], updated[swapIdx]] = [updated[swapIdx], updated[idx]];

      // Reassign sequential ranks
      updated.forEach((admin, i) => { admin.rank = i + 1; });

      // Persist both swapped admins
      updateRank(updated[idx].admin_id, updated[idx].rank);
      updateRank(updated[swapIdx].admin_id, updated[swapIdx].rank);

      return updated;
    });
  }, [updateRank]);

  // ── Build / refresh DataTable ────────────────────────────────────────────────
  useEffect(() => {
    const tableElement = tableRef.current;
    if (!tableElement || admins.length === 0) return;

    // Destroy existing instance before re-init (rank changes need full redraw)
    if ($.fn.DataTable.isDataTable(tableElement)) {
      $(tableElement).DataTable().destroy();
    }

    dtRef.current = $(tableElement).DataTable({
      dom: 'Bfrtip',
      paging: false,   // Disable pagination
      info: false,     // optional: hide "Showing X of Y entries"
      buttons: [
        { extend: 'copyHtml5',  className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'excelHtml5', className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'csvHtml5',   className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'pdfHtml5',   className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'print',      className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
      ],
      data: admins,
      order: [[0, 'asc']], // sort by Rank by default
      columns: [
        { data: 'rank',             title: 'Rank' },
        
        { data: 'chapter_name',     title: 'Chapter' },
        { data: 'name',             title: 'Name' },
        { data: 'mobileno',         title: 'Mobile No' },
        { data: 'email',            title: 'Email' },
        { data: 'role_name',        title: 'User Role' },
        { data: 'show_on_website',  title: 'Show on Website', orderable: false },
        { data: null,               title: 'Reorder',         orderable: false },
        { data: null,               title: 'Action',          orderable: false },
      ],
      columnDefs: [
        {
          // Rank column: show current rank badge
          targets: 0,
          render: (data) =>
            `<span class="badge bg-secondary rank-badge">${data ?? '—'}</span>`,
        },
        {
          // ID as clickable link
          targets: 1,
          render: (data, type, row) => 
            `<a href="#" class="edit-link" data-id="${row.admin_id}">${data}</a>`,
        },
        {
          // Show on Website toggle switch
          targets: 6,
          render: (data, type, row) => {
            const checked = data == 1 ? 'checked' : '';
            const badgeColor = data == 1 ? '#198754' : '#6c757d';
            const label = data == 1 ? 'Visible' : 'Hidden';
            return `
              <div class="d-flex align-items-center gap-2 justify-content-center">
                <div class="form-check form-switch mb-0">
                  <input
                    class="form-check-input visibility-toggle"
                    type="checkbox"
                    role="switch"
                    data-id="${row.admin_id}"
                    data-current="${data ?? 0}"
                    ${checked}
                    style="cursor:pointer; width:2.5em; height:1.3em;"
                  />
                </div>
                <span class="badge" style="background:${badgeColor}; font-size:0.75rem;">${label}</span>
              </div>`;
          },
        },
        {
          // Up / Down arrows for reordering
          targets: 7,
          render: (data, type, row) => `
            <div class="d-flex gap-1 justify-content-center">
              <button class="btn btn-sm btn-outline-secondary rank-up px-2 py-1" data-id="${row.admin_id}" title="Move Up">▲</button>
              <button class="btn btn-sm btn-outline-secondary rank-down px-2 py-1" data-id="${row.admin_id}" title="Move Down">▼</button>
            </div>`,
        },
        {
          // Edit / Delete actions
          targets: 8,
          render: (data, type, row) => {
            const editBtn = `<a href="#" class="edit-icon btn btn-warning btn-sm me-2 text-black" data-id="${row.admin_id}">
                               <i class="fas fa-edit"></i>&nbsp;Edit</a>`;
            const deleteBtn = roleid === 1
              ? `<a href="#" class="delete-icon btn btn-danger btn-sm" style="color:white" data-id="${row.admin_id}">
                   <i class="fas fa-trash"></i>&nbsp;Delete</a>`
              : '';
            return `${editBtn}${deleteBtn}`;
          },
        },
      ],
    });

    // ── Event handlers ────────────────────────────────────────────────────────
    $(document).off('click', '.rank-up');
    $(document).off('click', '.rank-down');
    $(document).off('click', '.edit-icon');
    $(document).off('click', '.edit-link');
    $(document).off('click', '.delete-icon');
    $(document).off('change', '.visibility-toggle');

    $(document).on('change', '.visibility-toggle', function () {
      const adminId = parseInt($(this).data('id'));
      const current = parseInt($(this).data('current'));
      toggleVisibility(adminId, current);
    });

    $(document).on('click', '.rank-up', function (e) {
      e.preventDefault();
      moveRank(parseInt($(this).data('id')), 'up');
    });

    $(document).on('click', '.rank-down', function (e) {
      e.preventDefault();
      moveRank(parseInt($(this).data('id')), 'down');
    });

    $(document).on('click', '.edit-icon', function (e) {
      e.preventDefault();
      navigate('/admin/EditAdmin', { state: { adminId: $(this).data('id'), edit: true } });
    });

    $(document).on('click', '.edit-link', function (e) {
      e.preventDefault();
      navigate('/admin/EditAdmin', { state: { adminId: $(this).data('id') } });
    });

    $(document).on('click', '.delete-icon', function (e) {
      e.preventDefault();
      const adminId = $(this).data('id');
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.isConfirmed) {
          const token = sessionStorage.getItem("token");
          $.ajax({
            url: `${baseurl}/admin/delete/${adminId}`,
            type: 'DELETE',
            headers: { 'Authorization': token, 'Content-Type': 'application/json' },
            success: (response) => {
              Swal.fire('Deleted!', response.message, 'success').then(fetchAdmin);
            },
            error: (xhr) => {
              Swal.fire('Error!', xhr.responseJSON?.message || 'Error deleting admin.', 'error');
            },
          });
        }
      });
    });

    return () => {
      if ($.fn.DataTable.isDataTable(tableRef.current)) {
        $(tableRef.current).DataTable().destroy();
      }
      $(document).off('click', '.rank-up');
      $(document).off('click', '.rank-down');
      $(document).off('click', '.edit-icon');
      $(document).off('click', '.edit-link');
      $(document).off('click', '.delete-icon');
      $(document).off('change', '.visibility-toggle');
    };
  }, [admins, navigate, roleid, moveRank, fetchAdmin]);

  return (
    <div className="content container-fluid">
      <div className="container mt-4">
        <div className="profile-section">

          {/* Header row */}
          <div className="row mb-3 align-items-center">
            <div className="col">
              <h5 className="mb-0 fw-bold">Admin List</h5>
              <small className="text-muted">
                Use ▲ ▼ arrows to reorder how admins appear on the website (Office Bearers &amp; Chapter Presidents).
              </small>
            </div>
            <div className="col-auto">
              <Link to="/admin/Addadmin" className="btn btn-primary btn-sm">
                <i className="bx bxs-plus-circle me-1"></i> Add Admin
              </Link>
            </div>
          </div>

          {/* Info legend */}
          <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
            <strong>ℹ️ Tips:</strong>&nbsp;
            <span>Use ▲ ▼ to reorder display position on the website (Rank 1 = first).</span>
            &nbsp;|&nbsp;
            <span>Use the <strong>Show on Website</strong> toggle to hide/show an admin on the public site without deleting them.</span>
          </div>

          <div className="list">
            <div className="row">
              <div className="col-12" style={{ overflow: 'auto' }}>
                <table
                  ref={tableRef}
                  className="table table-striped table-bordered"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminList;